import { Suspense, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Loading from "../../../../common/components/ui/loading/Loading";
import {
  IMaintenance,
  MAINTENANCE_STATUS,
  MaintenanceType,
} from "../../../../types/maintenance.type";
import {
  TableFilters,
  TableFiltersState,
} from "../../../../common/components/ui/table/TableFilters";
import GenericTable from "../../../../common/components/ui/table/GenericTable";
import TableFooter from "../../../../common/components/ui/table/TableFooter";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import { getMaintenance } from "../../../../common/services/maintenance.api";
import PendenciesMaintenance from "../PendenciesMaintenance";
import ModalCreateMaintenance from "../modals/ModalCreateMaintenance";
import { formatDateAndTime } from "../../../../common/helpers/date.helpers";
import ModalEditMaintenance from "../modals/ModalEditMaintenance"; // MODAL DE ATUALIZAÇÃO STATUS
import ModalBudgetMaintenance from "../modals/ModallBudgetMaintenance";
import ModalStartMaintenance from "../modals/ModalStartMaintenance";
import ModalFinalizeMaintenance from "../modals/ModalFinalizeMaintenance";
import { IVehicle } from "../../../../types/vehicle.type";
import ModalGeneratePDFMaintenance from "../modals/ModalGeneratePDFMaintenance";
import ModalEditTicketOrMechanic from "../modals/ModalEditTicketOrMechanic";
import { getCompanySigla } from "@/common/helpers/company.helpers";
import { AuthContext } from "../../../../common/components/auth/authContext/AuthContext";
import { useCompanyStore } from "@/common/states/store/Company";

const STATUS_LABELS = {
  [MAINTENANCE_STATUS.Screening]: "Triagem",
  [MAINTENANCE_STATUS.Pending]: "Pendente",
  [MAINTENANCE_STATUS.UNDER_REVIEW]: "Iniciada",
  [MAINTENANCE_STATUS.Finalize]: "Finalizado",
};

function getStatusLabels(status: MAINTENANCE_STATUS) {
  return STATUS_LABELS[status] || status;
}

const EMPTY_ACTION = { eventName: "", data: undefined };
const headers = {
  companyId: "Cidade",
  licensePlate: "Placa",
  waitingTicket: "Senha",
  status: "Status",
  mechanic: "Mecânico",
  name: "Cliente",
  typePlanRenter: "Plano do cliente",
  updatedAt: "Atualizado em",
  createdAt: "Criado em",
  collision: "Colisão",
  typeMaintenance: "Tipo de Manutenção",
};

function TableMaintenance() {
  useContext(AuthContext);
  const navigate = useNavigate();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [maintenance, setMaintenance] = useState<IMaintenance[]>([]);
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-updatedAt",
  });

  const { filterMode, selectedCompanies, selectedRegion, selectedUf } =
    useCompanyStore();
  useEffect(() => {
    async function loadMaintenance(filters, page) {
      setIsLoading(true);
      const result = await getMaintenance({ ...filters, page });
      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });
      if (!result.ok) {
        notifyError(
          (result as { message?: string })?.message || "unknown error"
        );
        notifyError(
          (result as { message?: string })?.message || "unknown error"
        );
        return;
      }
      const sortedData = (result.data as IMaintenance[]).sort((a, b) => {
        if (a.waitingTicket && b.waitingTicket) {
          return a.waitingTicket - b.waitingTicket;
        }
        return 0;
      });
      setMaintenance(sortedData);
      setIsLoading(false);
    }
    loadMaintenance(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, filterMode, selectedCompanies, selectedRegion, selectedUf]);

  const actions = [
    {
      label: "Triagem",
      action: (data) =>
        setAction({
          eventName: "screening",
          data: (data as MaintenanceType)._id,
        }),
      disabled: (data) =>
        [MAINTENANCE_STATUS.Finalize, MAINTENANCE_STATUS.Completed].includes(
          (data as MaintenanceType).status as MAINTENANCE_STATUS
        ),
      permission: "edit:maintenance",
    },
    {
      label: "Peças",
      action: (data) => setAction({ eventName: "budget", data: data }),
      disabled: (data) =>
        [
          MAINTENANCE_STATUS.Screening,
          MAINTENANCE_STATUS.Finalize,
          MAINTENANCE_STATUS.Completed,
        ].includes((data as MaintenanceType).status as MAINTENANCE_STATUS),
      permission: "edit:maintenance",
    },
    {
      label: "Iniciar",
      action: (data) =>
        setAction({ eventName: "startMaintenance", data: data }),
      disabled: (data) =>
        MAINTENANCE_STATUS.Pending !== (data as MaintenanceType).status,
      permission: "edit:maintenance",
    },
    {
      label: "Concluir",
      action: (data) => setAction({ eventName: "finalize", data: data }),
      disabled: (data) => {
        const maintenance = data as IMaintenance;
        const isUnderReview =
          maintenance.status === MAINTENANCE_STATUS.UNDER_REVIEW;
        const isDiagnosisPending =
          maintenance.status === MAINTENANCE_STATUS.Pending &&
          Boolean(maintenance.diagnosis);

        return !isUnderReview && !isDiagnosisPending;
      },
      permission: "edit:maintenance",
    },
    {
      label: "PDF",
      action: (data) => setAction({ eventName: "pdf", data: data }),
      disabled: (data) =>
        MAINTENANCE_STATUS.Pending === (data as MaintenanceType).status,
      permission: "edit:maintenance",
    },
    {
      label: "Editar Senha/Mecânico",
      action: (data) => setAction({ eventName: "edit", data: data }),
      disabled: (data) =>
        MAINTENANCE_STATUS.Finalize === (data as MaintenanceType).status,
      permission: "edit:maintenance-ticket-or-mechanic",
    },
  ];

  const isAction = (eventName: string) => action.eventName === eventName;
  const cleanActionData = () => {
    handleSearch(filters);
    setAction(EMPTY_ACTION);
  };
  const handleSearch = ({ search, status }) => {
    const newState = Object.assign({}, filters, { search, status });
    setFilters(newState);
  };
  function onMaintenanceCreated(maintenanceId: string) {
    handleSearch(filters);
    setAction({
      eventName: "screening",
      data: maintenanceId,
    });
  }

  const dataKey = (data: MaintenanceType) => data._id;

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Manutenções cadastradas"
          placeholder="Digite o nome do cliente, a placa, o mecânico, o chassi ou a senha."
          statusOptions={STATUS_LABELS}
          newItemLabel="Nova Manutenção"
          onNewItemClick={() =>
            setAction({ eventName: "create", data: undefined })
          }
          onSearch={handleFilterChange}
          entity="maintenance"
          tableData={maintenance}
        />

        <GenericTable
          headers={headers}
          data={maintenance}
          actions={actions}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              orderBy,
            }));
          }}
          renders={
            {
              companyId: (companyId) => {
                return getCompanySigla(companyId);
              },
              licensePlate: (licensePlate, vehicle) => (
                <a
                  className="text-blue-600 hover:text-bluePrimary cursor-pointer"
                  onClick={() =>
                    navigate(`/veiculos/${(vehicle as IVehicle).licensePlate}`)
                  }
                  target="_blank"
                >
                  {licensePlate}
                </a>
              ),
              waitingTicket: (waitingTicket) => waitingTicket || "-",
              status: getStatusLabels,
              mechanic: (mechanic) => mechanic || "-",
              collision: (_value, row) => {
                const isCollision = Boolean(
                  (row as IMaintenance | undefined)?.collision
                );

                return (
                  <span
                    title={isCollision ? "Colisão: Sim" : "Colisão: Não"}
                    className={[
                      "inline-flex items-center justify-center",
                      "h-4 w-4 rounded-[3px] border select-none",
                      isCollision
                        ? "bg-green-500 border-green-500 text-white"
                        : "bg-white border-gray-400 text-transparent",
                    ].join(" ")}
                    style={{ fontSize: 11, lineHeight: "1" }}
                  >
                    ✓
                  </span>
                );
              },
              pendencies: (_, maintenance) => (
                <PendenciesMaintenance
                  maintenance={maintenance as MaintenanceType}
                />
              ),
              updatedAt: formatDateAndTime,
              createdAt: formatDateAndTime,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          }
          entity="maintenance"
        />
        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />
        {isAction("create") && (
          <ModalCreateMaintenance
            onClosed={cleanActionData}
            onSave={onMaintenanceCreated}
          />
        )}
        {isAction("screening") && (
          <ModalEditMaintenance
            maintenanceId={action.data as string}
            onClose={cleanActionData}
          />
        )}
        {isAction("budget") && (
          <ModalBudgetMaintenance
            key={dataKey(action.data as MaintenanceType)}
            data={action.data as IMaintenance}
            onClose={cleanActionData}
          />
        )}
        {isAction("startMaintenance") && (
          <ModalStartMaintenance
            maintenance={action.data as { _id: string; licensePlate: string }}
            data={action.data as IMaintenance}
            onClose={cleanActionData}
          />
        )}
        {isAction("finalize") && (
          <ModalFinalizeMaintenance
            maintenance={action.data as { _id: string; licensePlate: string }}
            data={action.data as IMaintenance}
            onClose={cleanActionData}
          />
        )}
        {isAction("pdf") && (
          <ModalGeneratePDFMaintenance
            data={action.data as IMaintenance}
            onClose={cleanActionData}
          />
        )}
        {isAction("edit") && (
          <ModalEditTicketOrMechanic
            maintenance={action.data as { _id: string; licensePlate: string }}
            data={action.data as IMaintenance}
            onClose={cleanActionData}
          />
        )}
      </div>
    </Suspense>
  );
}

export default TableMaintenance;
